import {
  Button,
  Checkbox,
  Color,
  Flex,
  FormItemsGroup,
  Icon,
  Input,
  Modal,
  ModalProps,
  Spacing,
  Tooltip,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { ContactPerson } from '@features/contacts/types/contactPerson'
import { joinPersonName } from '@features/contacts/utils/joinPersonName'

import { useForm } from '../../../../../../../../hooks'
import { useCreateContactPerson } from '../../hooks/useCreateContactPerson'
import { useUpdateContactPerson } from '../../hooks/useUpdateContactPerson'
import {
  ContactPersonFormSchema,
  ContactPersonFormValues,
  getContactPersonFormSchema,
  parseContactPersonName,
} from './utils/contactPersonFormData'

export const CREATE_CONTACT_PERSON_MODAL_ID = 'create-contact-person-from-detail'

interface ContactPersonModalProps extends ModalProps {
  contactId: string
  contactPerson?: Pick<ContactPerson, 'id' | 'name' | 'email' | 'isPrimary'>
}

const ContactPersonModalComponent = ({
  contactId,
  contactPerson,
  ...modalProps
}: ContactPersonModalProps): ReactElement => {
  const { t } = useTranslation()
  const { close } = useModal(modalProps.id)
  const { createContactPerson, isProcessing: isCreating } = useCreateContactPerson()
  const { updateContactPerson, isProcessing: isUpdating } = useUpdateContactPerson()

  const isEditMode = !!contactPerson?.id
  const isLoading = isCreating || isUpdating
  const title = isEditMode ? t('customer_modal.edit_contact.header') : t('customer_modal.add_contact.header')

  const validationSchema = useMemo(() => getContactPersonFormSchema(), [])

  const parsedName = useMemo(() => parseContactPersonName(contactPerson?.name), [contactPerson?.name])

  const { Form, FormItem, handleSubmit } = useForm<ContactPersonFormSchema>({
    defaultValues: {
      firstName: parsedName.firstName,
      lastName: parsedName.lastName,
      email: contactPerson?.email || '',
      isPrimary: contactPerson?.isPrimary || false,
    },
    validationSchema,
  })

  const handleFormSubmit = useCallback(
    (values: ContactPersonFormValues) => {
      const name = joinPersonName(values.firstName, values.lastName)
      const payload = { name, email: values.email, isPrimary: values.isPrimary }

      if (isEditMode && contactPerson?.id) {
        updateContactPerson({ id: contactPerson.id, payload }, { onSuccess: close })
      } else {
        createContactPerson({ contactId, ...payload }, { onSuccess: close })
      }
    },
    [isEditMode, contactPerson?.id, contactId, createContactPerson, updateContactPerson, close],
  )

  const submitForm = useCallback(() => {
    handleSubmit(handleFormSubmit)()
  }, [handleSubmit, handleFormSubmit])

  return (
    <Modal {...modalProps} size="s">
      <Modal.Header title={title} />
      <Modal.Body>
        <Form>
          <FormItemsGroup rowSpacing={Spacing.S12}>
            <FormItemsGroup itemsInRow={2}>
              <FormItem
                label={t('customer_modal.form.first_name.label')}
                name="firstName"
                render={({ field }) => <Input {...field} autoFocus />}
              />
              <FormItem
                label={t('customer_modal.form.last_name.label')}
                name="lastName"
                render={({ field }) => <Input {...field} />}
              />
            </FormItemsGroup>
            <FormItem
              label={t('customer_modal.form.email.label')}
              name="email"
              render={({ field }) => <Input {...field} />}
            />
            <Flex gap={Spacing.S8} alignItems="center">
              <FormItem
                name="isPrimary"
                render={({ field: { value, onChange } }) => (
                  <Checkbox checked={value} onChange={onChange}>
                    {t('customer_modal.form.company_persons.primary_checkbox.label')}
                  </Checkbox>
                )}
              />
              <Tooltip label={t('customer_modal.form.company_persons.primary_checkbox.tooltip')} placement="top">
                <Icon color={Color.Grey300} icon="info" />
              </Tooltip>
            </Flex>
          </FormItemsGroup>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={close} variant="text">
          {t('cancel')}
        </Button>
        <Button loading={isLoading} onClick={submitForm}>
          {t('save')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export const ContactPersonModal = withModalConditionalRender(ContactPersonModalComponent)
